import { createContext, useContext } from 'react';
import { startAuthentication, startRegistration, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';
import { api } from './api.ts';

export type Me = {
  id: number; email: string; isAdmin: boolean; passkeys: number; recoveryCodesLeft: number;
  passwordChangeNeedsCurrent: boolean; signupMode: 'invite' | 'open';
};

export const SessionContext = createContext<{ me: Me; refresh: () => Promise<void>; signOut: () => Promise<void> }>(null!);
export const useSession = () => useContext(SessionContext);

const PASSKEY_FLAG = 'tj_passkey_device';

export function deviceHasPasskey() {
  try { return localStorage.getItem(PASSKEY_FLAG) === '1'; } catch { return false; }
}
function setDeviceHasPasskey(on: boolean) {
  try { on ? localStorage.setItem(PASSKEY_FLAG, '1') : localStorage.removeItem(PASSKEY_FLAG); } catch { /* ignore */ }
}

export async function canUseBiometrics() {
  if (!browserSupportsWebAuthn()) return false;
  try { return await platformAuthenticatorIsAvailable(); } catch { return false; }
}

/** Friendly label for the device's biometric, e.g. "Face ID" on iPhone. */
export function biometricName() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'Face ID';
  if (/Macintosh/.test(ua)) return 'Touch ID';
  if (/Windows/.test(ua)) return 'Windows Hello';
  if (/Android/.test(ua)) return 'fingerprint / face unlock';
  return 'passkey';
}

export function deviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'This device';
}

export async function registerPasskey() {
  const optionsJSON = await api.post<any>('/api/auth/passkeys/register/options');
  const response = await startRegistration({ optionsJSON });
  await api.post('/api/auth/passkeys/register/verify', { response, name: deviceName() });
  setDeviceHasPasskey(true);
}

export async function signInWithPasskey() {
  const optionsJSON = await api.post<any>('/api/auth/passkeys/login/options');
  const response = await startAuthentication({ optionsJSON });
  await api.post('/api/auth/passkeys/login/verify', { response });
  setDeviceHasPasskey(true);
}

export function forgetDevicePasskey() {
  setDeviceHasPasskey(false);
}

/** Turns WebAuthn's DOMExceptions into something readable; returns null when the user just cancelled. */
export function passkeyError(err: unknown): string | null {
  const e = err as { name?: string; message?: string };
  if (e?.name === 'NotAllowedError' || e?.name === 'AbortError') return null;
  if (e?.name === 'InvalidStateError') return 'This device is already set up.';
  if (e?.name === 'SecurityError') return 'Face ID needs the site to be served over HTTPS (or localhost).';
  return e?.message ?? 'Something went wrong.';
}
